import React, { useState, useEffect } from 'react';
import Window from './Window';
import About from './About';
import Projects from './Projects';
import Contact from './Contact';
import InteractiveTerminal from './InteractiveTerminal';

// Beautiful SVG Icons used throughout the OS desktop
const Icons = {
  Terminal: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"></polyline><line x1="12" y1="19" x2="20" y2="19"></line></svg>
  ),
  User: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
  ),
  Folder: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
  ),
  Mail: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
  ),
  Globe: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
  ),
  Activity: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
  )
};

function Desktop({ onLocateProject, mapTheme, setMapTheme }) {
  // Desktop Window State
  const [windows, setWindows] = useState({
    about: { title: 'about_me.txt', isOpen: true, isMinimized: false, isMaximized: false },
    projects: { title: 'projects.json', isOpen: true, isMinimized: false, isMaximized: false },
    contact: { title: 'contact_info.sh', isOpen: false, isMinimized: false, isMaximized: false },
    terminal: { title: 'terminal_cli.sh', isOpen: true, isMinimized: false, isMaximized: false }
  });

  const [activeWindowId, setActiveWindowId] = useState('about');
  const [timeString, setTimeString] = useState('');

  // Mobile UI Navigation State
  const [mobileTab, setMobileTab] = useState('home');

  // Clock updating hook
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      const dateStr = now.toLocaleDateString('en-US', options);
      const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      setTimeString(`${dateStr}  ${timeStr}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const openWindow = (id) => {
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], isOpen: true, isMinimized: false }
    }));
    setActiveWindowId(id);
  };

  const closeWindow = (id) => {
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], isOpen: false }
    }));
  };

  const minimizeWindow = (id) => {
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], isMinimized: true }
    }));
  };

  const toggleMaximizeWindow = (id) => {
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], isMaximized: !prev[id].isMaximized }
    }));
  };

  const handleDockClick = (id) => {
    const win = windows[id];
    if (!win.isOpen) {
      openWindow(id);
    } else if (win.isMinimized) {
      // Restore
      setWindows(prev => ({
        ...prev,
        [id]: { ...prev[id], isMinimized: false }
      }));
      setActiveWindowId(id);
    } else if (activeWindowId === id) {
      // Minimize if already active & clicked again
      minimizeWindow(id);
    } else {
      // Focus if open but background
      setActiveWindowId(id);
    }
  };

  // Center on map & open projects window if marker is clicked
  const handleMapLocate = (projectKey) => {
    onLocateProject(projectKey);
    openWindow('projects');
    // We can dispatch scroll event or active highlighting in the projects tab
    setTimeout(() => {
      const card = document.getElementById(`project-card-${projectKey}`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.style.borderColor = '#22c55e';
        setTimeout(() => {
          card.style.borderColor = 'rgba(255, 255, 255, 0.05)';
        }, 2000);
      }
    }, 200);
  };

  // Mobile Map redirection
  const handleMobileLocate = (projectKey) => {
    setMobileTab('map');
    onLocateProject(projectKey);
  };

  return (
    <>
      {/* 🖥️ DESKTOP OS EXPERIENCE */}
      <div className="desktop-container">
        {/* Top Status Bar */}
        <div className="top-bar">
          <div className="top-bar-left">
            <span className="top-bar-brand">jeremy_roy</span>
            <div className="top-bar-mode">
              <span className="cursor-blink" style={{ width: '6px', height: '10px', margin: 0 }}></span>
              <span>Online</span>
            </div>
            <button
              onClick={() => setMapTheme(mapTheme === 'dark' ? 'light' : 'dark')}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
                color: 'var(--color-text)',
                padding: '2px 8px',
                fontSize: '0.7rem',
                fontFamily: 'monospace',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              className="map-theme-toggle"
            >
              <span>Map Style:</span>
              <span style={{ color: mapTheme === 'light' ? '#22c55e' : '#38bdf8', fontWeight: 'bold' }}>
                {mapTheme.toUpperCase()}
              </span>
            </button>
          </div>
          <div className="top-bar-right">
            <span>{timeString}</span>
          </div>
        </div>

        {/* Workspace holding Shortcuts & Windows */}
        <div className="desktop-workspace">
          {/* Desktop Shortcut Icons */}
          <div className="desktop-icons">
            <button className="desktop-icon" onClick={() => openWindow('about')}>
              <div className="desktop-icon-symbol"><Icons.User /></div>
              <span className="desktop-icon-label">about_me.txt</span>
            </button>
            <button className="desktop-icon" onClick={() => openWindow('projects')}>
              <div className="desktop-icon-symbol"><Icons.Folder /></div>
              <span className="desktop-icon-label">projects.json</span>
            </button>
            <button className="desktop-icon" onClick={() => openWindow('contact')}>
              <div className="desktop-icon-symbol"><Icons.Mail /></div>
              <span className="desktop-icon-label">contact.sh</span>
            </button>
            <button className="desktop-icon" onClick={() => openWindow('terminal')}>
              <div className="desktop-icon-symbol"><Icons.Terminal /></div>
              <span className="desktop-icon-label">terminal_cli</span>
            </button>
          </div>

          {/* Windows Overlay rendering components */}
          <Window
            id="about"
            title="about_me.txt — edit"
            isOpen={windows.about.isOpen}
            isMinimized={windows.about.isMinimized}
            isMaximized={windows.about.isMaximized}
            onClose={closeWindow}
            onMinimize={minimizeWindow}
            onMaximize={toggleMaximizeWindow}
            activeWindowId={activeWindowId}
            setActiveWindow={setActiveWindowId}
            defaultPosition={{ x: 140, y: 70 }}
            defaultSize={{ width: 620, height: 400 }}
          >
            <About />
          </Window>

          <Window
            id="projects"
            title="projects.json — readonly"
            isOpen={windows.projects.isOpen}
            isMinimized={windows.projects.isMinimized}
            isMaximized={windows.projects.isMaximized}
            onClose={closeWindow}
            onMinimize={minimizeWindow}
            onMaximize={toggleMaximizeWindow}
            activeWindowId={activeWindowId}
            setActiveWindow={setActiveWindowId}
            defaultPosition={{ x: 200, y: 150 }}
            defaultSize={{ width: 660, height: 480 }}
          >
            <Projects onLocateOnMap={handleMapLocate} />
          </Window>

          <Window
            id="contact"
            title="contact_info.sh — execute"
            isOpen={windows.contact.isOpen}
            isMinimized={windows.contact.isMinimized}
            isMaximized={windows.contact.isMaximized}
            onClose={closeWindow}
            onMinimize={minimizeWindow}
            onMaximize={toggleMaximizeWindow}
            activeWindowId={activeWindowId}
            setActiveWindow={setActiveWindowId}
            defaultPosition={{ x: 340, y: 100 }}
            defaultSize={{ width: 500, height: 320 }}
          >
            <Contact />
          </Window>

          <Window
            id="terminal"
            title="bash — zsh — terminal_cli"
            isOpen={windows.terminal.isOpen}
            isMinimized={windows.terminal.isMinimized}
            isMaximized={windows.terminal.isMaximized}
            onClose={closeWindow}
            onMinimize={minimizeWindow}
            onMaximize={toggleMaximizeWindow}
            activeWindowId={activeWindowId}
            setActiveWindow={setActiveWindowId}
            defaultPosition={{ x: 100, y: 250 }}
            defaultSize={{ width: 640, height: 420 }}
          >
            <InteractiveTerminal
              onLocateProject={handleMapLocate}
              onOpenWindow={openWindow}
            />
          </Window>
        </div>

        {/* OS Bottom Dock */}
        <div className="dock-container">
          <div className="dock">
            <div
              className={`dock-item ${windows.about.isOpen && !windows.about.isMinimized ? 'active' : ''}`}
              data-tooltip="About Me"
              onClick={() => handleDockClick('about')}
            >
              <Icons.User />
              {windows.about.isOpen && <span className="dock-dot"></span>}
            </div>
            <div
              className={`dock-item ${windows.projects.isOpen && !windows.projects.isMinimized ? 'active' : ''}`}
              data-tooltip="Projects"
              onClick={() => handleDockClick('projects')}
            >
              <Icons.Folder />
              {windows.projects.isOpen && <span className="dock-dot"></span>}
            </div>
            <div
              className={`dock-item ${windows.contact.isOpen && !windows.contact.isMinimized ? 'active' : ''}`}
              data-tooltip="Contact Details"
              onClick={() => handleDockClick('contact')}
            >
              <Icons.Mail />
              {windows.contact.isOpen && <span className="dock-dot"></span>}
            </div>
            <div
              className={`dock-item ${windows.terminal.isOpen && !windows.terminal.isMinimized ? 'active' : ''}`}
              data-tooltip="Terminal Command Line"
              onClick={() => handleDockClick('terminal')}
            >
              <Icons.Terminal />
              {windows.terminal.isOpen && <span className="dock-dot"></span>}
            </div>
          </div>
        </div>
      </div>

      {/* 📱 MOBILE APPLICATION DASHBOARD EXPERIENCE */}
      <div
        className="mobile-layout"
        style={{
          background: mobileTab === 'map' ? 'transparent' : 'var(--color-bg)',
          pointerEvents: mobileTab === 'map' ? 'none' : 'auto'
        }}
      >
        <div className="mobile-header" style={{ pointerEvents: 'auto' }}>
          <div className="mobile-title">
            <h1>Jeremy Roy</h1>
            <div className="mobile-subtitle">Data & Mechanical Engineer</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setMapTheme(mapTheme === 'dark' ? 'light' : 'dark')}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '4px',
                color: 'var(--color-text)',
                padding: '3px 8px',
                fontSize: '0.68rem',
                fontFamily: 'monospace',
                cursor: 'pointer'
              }}
            >
              Map: {mapTheme.toUpperCase()}
            </button>
            <div className="top-bar-mode" style={{ fontSize: '0.68rem', padding: '1px 6px' }}>
              <span>Online</span>
            </div>
          </div>
        </div>

        <div className="mobile-content">
          {mobileTab === 'home' && (
            <div className="mobile-panel">
              <div className="terminal" style={{ height: 'auto', background: 'rgba(30, 41, 59, 0.4)', border: '1px solid rgba(56, 189, 248, 0.15)', borderRadius: '8px', overflow: 'hidden' }}>
                <div className="terminal-header" style={{ background: 'rgba(30, 41, 59, 0.8)', padding: '6px 12px', color: '#38bdf8', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  system_check.sh
                </div>
                <div className="terminal-body" style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  <p style={{ color: '#22c55e' }}>➜ System: Booting portfolio...</p>
                  <p style={{ color: '#94a3b8' }}>➜ Role: Data & Mechanical Engineer</p>
                  <p style={{ color: '#94a3b8' }}>➜ Expertise: GIS visualisations & telemetry</p>
                  <div style={{ margin: '1rem 0', borderBottom: '1px dashed #1e293b' }}></div>
                  <p style={{ lineHeight: '1.4' }}>"Qualified Mechanical Engineer and experienced Data Engineer managing telemetry analytics for major UK water utilities."</p>
                  <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button
                      onClick={() => setMobileTab('about')}
                      style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.25)', color: '#38bdf8', padding: '8px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'monospace' }}
                    >
                      $ read about_me.txt
                    </button>
                    <button
                      onClick={() => setMobileTab('projects')}
                      style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#22c55e', padding: '8px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'monospace' }}
                    >
                      $ query projects.json
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mobileTab === 'about' && (
            <div className="mobile-panel">
              <About />
            </div>
          )}

          {mobileTab === 'projects' && (
            <div className="mobile-panel">
              <Projects onLocateOnMap={handleMobileLocate} />
            </div>
          )}

          {mobileTab === 'contact' && (
            <div className="mobile-panel">
              <Contact />
            </div>
          )}

          {mobileTab === 'map' && (
            // Empty placeholder div with pointer-events: none, allowing map interaction behind it
            <div className="mobile-map-container" style={{ pointerEvents: 'none' }}>
              <div
                style={{
                  position: 'absolute',
                  bottom: '1rem',
                  left: '1rem',
                  right: '1rem',
                  background: 'rgba(15, 23, 42, 0.85)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '0.75rem',
                  color: 'var(--color-text-muted)',
                  textAlign: 'center',
                  pointerEvents: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }}
              >
                🌍 Drag & zoom map to inspect project telemetry points.
              </div>
            </div>
          )}
        </div>

        {/* Bottom tab bar for mobile viewports */}
        <div className="mobile-navbar" style={{ pointerEvents: 'auto' }}>
          <button
            className={`mobile-nav-item ${mobileTab === 'home' ? 'active' : ''}`}
            onClick={() => setMobileTab('home')}
          >
            <span className="mobile-nav-icon"><Icons.Activity /></span>
            <span>Home</span>
          </button>
          <button
            className={`mobile-nav-item ${mobileTab === 'about' ? 'active' : ''}`}
            onClick={() => setMobileTab('about')}
          >
            <span className="mobile-nav-icon"><Icons.User /></span>
            <span>About</span>
          </button>
          <button
            className={`mobile-nav-item ${mobileTab === 'projects' ? 'active' : ''}`}
            onClick={() => setMobileTab('projects')}
          >
            <span className="mobile-nav-icon"><Icons.Folder /></span>
            <span>Projects</span>
          </button>
          <button
            className={`mobile-nav-item ${mobileTab === 'contact' ? 'active' : ''}`}
            onClick={() => setMobileTab('contact')}
          >
            <span className="mobile-nav-icon"><Icons.Mail /></span>
            <span>Contact</span>
          </button>
          <button
            className={`mobile-nav-item ${mobileTab === 'map' ? 'active' : ''}`}
            onClick={() => setMobileTab('map')}
          >
            <span className="mobile-nav-icon"><Icons.Globe /></span>
            <span>Map</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default Desktop;
